import { useEffect, useMemo, useRef, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminSchoolRollout.css";

type Assessment={
  versionId:string;
  instrumentName:string;
  title:string;
  versionLabel:string;
  gradeMin?:number|null;
  gradeMax?:number|null;
};

type Badge={
  badgeKey:string;
  teacherName:string;
  studentName:string;
  category:string;
};

type BatchSummary={
  batchId:string;
  batchName:string;
  expectedYearCode?:string|null;
  assessmentVersionId?:string|null;
  assessmentTitle?:string|null;
  badgeKeys:string[];
  status:string;
  createdAt:string;
  schoolCount:number;
  claimedInvites:number;
  studentReadySchools:number;
};

type Workspace={
  root:{organizationId:string;name:string;code?:string|null;type:string};
  assessments:Assessment[];
  badges:Badge[];
  batches:BatchSummary[];
};

type Checklist={
  schoolRegistered:boolean;
  invitationClaimed:boolean;
  identityLinked:boolean;
  academicYearReady:boolean;
  classesReady:boolean;
  studentsReady:boolean;
  assessmentReady:boolean;
  badgePackageReady:boolean;
};

type RolloutItem={
  itemId:string;
  schoolId:string;
  schoolCode?:string|null;
  schoolName:string;
  teacherEmail:string;
  schoolWasExisting:boolean;
  invitationId?:string|null;
  invitationStatus?:string|null;
  invitationExpiresAt?:string|null;
  claimedAt?:string|null;
  identityLinked:boolean;
  academicYearId?:string|null;
  yearCode?:string|null;
  academicYearStatus?:string|null;
  classCount:number;
  studentCount:number;
  assessmentAssigned:number;
  assessmentReady:boolean;
  badgePackageReady:boolean;
  activationState:string;
  checklist:Checklist;
};

type BatchDetail={
  batch:{
    batchId:string;
    batchName:string;
    expectedYearCode?:string|null;
    assessmentVersionId?:string|null;
    assessmentTitle?:string|null;
    badgeKeys:string[];
    status:string;
    createdAt:string;
  };
  summary:{
    schoolCount:number;
    liveSchools:number;
    invitationPending:number;
    accountPending:number;
    centerSetupPending:number;
    studentDataPending:number;
    packagePending:number;
  };
  items:RolloutItem[];
};

type InviteLink={
  rolloutItemId?:string;
  schoolId:string;
  schoolCode?:string;
  schoolName?:string;
  teacherEmail:string;
  invitationId:string;
  claimUrl:string;
  expiresAt:string;
};

type CreateBatchResult={
  batchId:string;
  batchName:string;
  schoolCount:number;
  items:Array<{
    schoolId:string;
    schoolCode:string;
    schoolName:string;
    teacherEmail:string;
    schoolWasExisting:boolean;
    invitationId:string;
    claimUrl:string;
    expiresAt:string;
  }>;
};

function formatDate(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function activationLabel(value:string){
  const labels:Record<string,string>={
    INVITATION_PENDING:"초대 수령 대기",
    ACCOUNT_LINK_PENDING:"Google 연결 대기",
    ACADEMIC_YEAR_PENDING:"학년도 설정 대기",
    CLASS_SETUP_PENDING:"반 편성 대기",
    STUDENT_IMPORT_PENDING:"학생 등록 대기",
    ASSESSMENT_PACKAGE_PENDING:"검사 배포 대기",
    BADGE_PACKAGE_CHECK:"배지 패키지 확인",
    LIVE:"개통 완료"
  };
  return labels[value] || value;
}

function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function parseCsvLine(line:string):string[]{
  const values:string[]=[];
  let current="";
  let quoted=false;

  for(let i=0;i<line.length;i++){
    const char=line[i];
    if(char==='"'){
      if(quoted && line[i+1]==='"'){
        current+='"';
        i++;
      }else{
        quoted=!quoted;
      }
      continue;
    }
    if(char==="," && !quoted){
      values.push(current.trim());
      current="";
      continue;
    }
    current+=char;
  }
  values.push(current.trim());
  return values;
}

function parseSchoolCsv(text:string){
  const lines=text
    .replace(/^\uFEFF/,"")
    .split(/\r?\n/)
    .map((line)=>line.trim())
    .filter(Boolean);

  if(!lines.length)return [];

  const first=parseCsvLine(lines[0]).map((value)=>value.toLowerCase().replace(/\s/g,""));
  const hasHeader=
    first.includes("schoolcode") ||
    first.includes("학교코드") ||
    first.includes("teacheremail") ||
    first.includes("교사이메일");

  const rows=(hasHeader?lines.slice(1):lines).map((line,index)=>{
    const [schoolCode="",schoolName="",teacherEmail=""]=parseCsvLine(line);
    return {
      row:index+1,
      schoolCode:schoolCode.trim(),
      schoolName:schoolName.trim(),
      teacherEmail:teacherEmail.trim().toLowerCase()
    };
  });

  return rows;
}

function csvCell(value:unknown){
  const text=value==null?"":String(value);
  return '"'+text.replace(/"/g,'""')+'"';
}

function downloadCsv(rows:Array<Record<string,unknown>>,filename:string){
  if(!rows.length)return;
  const headers=Object.keys(rows[0]);
  const body=[
    headers.map(csvCell).join(","),
    ...rows.map((row)=>headers.map((key)=>csvCell(row[key])).join(","))
  ].join("\r\n");
  const blob=new Blob(["\uFEFF",body],{type:"text/csv;charset=utf-8"});
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a");
  a.href=url;
  a.download=filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function AdminSchoolRollout({
  client,
  rootOrganizationId
}:{client:PlatformClient;rootOrganizationId:string}){
  const [workspace,setWorkspace]=useState<Workspace|null>(null);
  const [selectedBatchId,setSelectedBatchId]=useState("");
  const [detail,setDetail]=useState<BatchDetail|null>(null);
  const [showCreate,setShowCreate]=useState(false);
  const [batchName,setBatchName]=useState("");
  const [yearCode,setYearCode]=useState("2026");
  const [assessmentVersionId,setAssessmentVersionId]=useState("");
  const [selectedBadges,setSelectedBadges]=useState<string[]>([]);
  const [csvText,setCsvText]=useState("");
  const [stateFilter,setStateFilter]=useState("ALL");
  const [query,setQuery]=useState("");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);
  const [inviteLinks,setInviteLinks]=useState<Record<string,InviteLink>>({});
  const fileRef=useRef<HTMLInputElement|null>(null);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("admin_school_rollout_workspace",{p_root_organization_id:rootOrganizationId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Workspace;
      setWorkspace(next);
      setSelectedBadges((current)=>current.length?current:next.badges.map((item)=>item.badgeKey));
      setSelectedBatchId((current)=>
        next.batches.some((item)=>item.batchId===current)
          ? current
          : next.batches[0]?.batchId || ""
      );
    });
    return()=>{active=false;};
  },[client,rootOrganizationId,refresh]);

  useEffect(()=>{
    if(!selectedBatchId){
      setDetail(null);
      return;
    }
    let active=true;
    client.rpc("admin_school_rollout_detail",{
      p_root_organization_id:rootOrganizationId,
      p_batch_id:selectedBatchId
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setDetail(data as BatchDetail);
    });
    return()=>{active=false;};
  },[client,rootOrganizationId,selectedBatchId,refresh]);

  const parsedRows=useMemo(()=>parseSchoolCsv(csvText),[csvText]);

  const csvValidation=useMemo(()=>{
    const codes=new Set<string>();
    const errors:string[]=[];
    parsedRows.forEach((row)=>{
      if(!row.schoolCode || !row.schoolName || !row.teacherEmail.includes("@")){
        errors.push(`${row.row}행 필수값 확인`);
      }
      const code=row.schoolCode.toUpperCase();
      if(codes.has(code))errors.push(`${row.row}행 학교코드 중복`);
      codes.add(code);
    });
    if(parsedRows.length>500)errors.push("한 Batch는 최대 500개교입니다.");
    return errors;
  },[parsedRows]);

  const visibleItems=useMemo(()=>{
    const keyword=query.trim().toLowerCase();
    return (detail?.items || []).filter((item)=>{
      if(stateFilter!=="ALL" && item.activationState!==stateFilter)return false;
      return !keyword || [item.schoolName,item.schoolCode || "",item.teacherEmail]
        .some((value)=>value.toLowerCase().includes(keyword));
    });
  },[detail,query,stateFilter]);

  const distributionCandidates=useMemo(
    ()=>detail?.items.filter((item)=>
      Boolean(detail.batch.assessmentVersionId)
      && item.studentCount>0
      && !item.assessmentReady
    ) || [],
    [detail]
  );

  function toggleBadge(key:string){
    setSelectedBadges((current)=>
      current.includes(key)?current.filter((item)=>item!==key):[...current,key]
    );
  }

  async function readFile(file:File){
    const text=await file.text();
    setCsvText(text);
  }

  function downloadTemplate(){
    downloadCsv([
      {schoolCode:"SCHOOL-001",schoolName:"예시중학교",teacherEmail:"career.teacher@example.com"}
    ],"RS_CAREER_school_rollout_template.csv");
  }

  async function createBatch(){
    if(!workspace || !parsedRows.length || csvValidation.length)return;
    setBusy("create");
    setError("");
    setMessage("");
    const {data,error}=await client.rpc("admin_create_school_rollout_batch",{
      p_root_organization_id:rootOrganizationId,
      p_batch_name:batchName.trim(),
      p_expected_year_code:yearCode.trim() || null,
      p_assessment_version_id:assessmentVersionId || null,
      p_badge_keys:selectedBadges,
      p_rows:parsedRows.map((row)=>({
        schoolCode:row.schoolCode,
        schoolName:row.schoolName,
        teacherEmail:row.teacherEmail
      }))
    });
    setBusy("");
    if(error){setError(error.message);return;}

    const result=data as CreateBatchResult;
    const nextLinks:Record<string,InviteLink>={};
    result.items.forEach((item)=>{
      nextLinks[item.schoolId]={
        schoolId:item.schoolId,
        schoolCode:item.schoolCode,
        schoolName:item.schoolName,
        teacherEmail:item.teacherEmail,
        invitationId:item.invitationId,
        claimUrl:item.claimUrl,
        expiresAt:item.expiresAt
      };
    });
    setInviteLinks(nextLinks);
    setSelectedBatchId(result.batchId);
    setBatchName("");
    setCsvText("");
    setShowCreate(false);
    setMessage(`${result.schoolCount}개 학교를 Rollout Batch에 등록하고 개통 담당교사 초대를 생성했습니다.`);
    setRefresh((value)=>value+1);
  }

  function downloadCurrentInviteLinks(){
    const rows=Object.values(inviteLinks);
    downloadCsv(rows.map((item)=>({
      schoolCode:item.schoolCode || "",
      schoolName:item.schoolName || "",
      teacherEmail:item.teacherEmail,
      claimUrl:item.claimUrl,
      expiresAt:item.expiresAt
    })),"RS_CAREER_rollout_teacher_invites.csv");
  }

  async function reissue(item:RolloutItem){
    setBusy("reissue:"+item.itemId);
    setError("");
    setMessage("");
    const {data,error}=await client.rpc("admin_reissue_rollout_teacher_invitation",{
      p_root_organization_id:rootOrganizationId,
      p_rollout_item_id:item.itemId
    });
    setBusy("");
    if(error){setError(error.message);return;}
    const result=data as InviteLink;
    setInviteLinks((current)=>({
      ...current,
      [item.schoolId]:{
        ...result,
        schoolId:item.schoolId,
        schoolCode:item.schoolCode || "",
        schoolName:item.schoolName
      }
    }));
    setMessage(`${item.schoolName} 개통 초대링크를 다시 발급했습니다.`);
    setRefresh((value)=>value+1);
  }

  async function distributeAssessment(){
    if(!detail?.batch.assessmentVersionId || !distributionCandidates.length)return;
    setBusy("distribution");
    setError("");
    setMessage("");

    try{
      let succeeded=0;
      let failed=0;
      for(let i=0;i<distributionCandidates.length;i+=100){
        const group=distributionCandidates.slice(i,i+100);
        const {data,error}=await client.rpc("admin_distribute_assessment_to_schools",{
          p_root_organization_id:rootOrganizationId,
          p_version_id:detail.batch.assessmentVersionId,
          p_school_ids:group.map((item)=>item.schoolId)
        });
        if(error)throw new Error(error.message);
        const result=data as {succeededCount:number;failedCount:number};
        succeeded+=result.succeededCount || 0;
        failed+=result.failedCount || 0;
      }
      setMessage(`${distributionCandidates.length}개 학교 검사 패키지 배포 완료 · 학생 성공 ${succeeded} · 실패 ${failed}`);
      setRefresh((value)=>value+1);
    }catch(err){
      setError(err instanceof Error?err.message:"검사 배포 중 오류가 발생했습니다.");
    }finally{
      setBusy("");
    }
  }

  if(!workspace)return <p className="muted">학교 Rollout 정보를 불러오는 중…</p>;

  const summary=detail?.summary || {
    schoolCount:0,liveSchools:0,invitationPending:0,accountPending:0,
    centerSetupPending:0,studentDataPending:0,packagePending:0
  };

  return (
    <section className="school-rollout-page">
      <header className="school-rollout-hero">
        <div>
          <p className="eyebrow">SCHOOL ONBOARDING & ROLLOUT CENTER</p>
          <h2>학교 개통·확산센터</h2>
          <p>
            학교를 Batch로 등록하고 개통 담당교사 초대, Google Claim, Center 초기설정,
            학생 데이터, 검사·Life Stamp 패키지까지 단계별로 관리합니다.
          </p>
        </div>
        <button className="primary" onClick={()=>setShowCreate((value)=>!value)}>
          {showCreate?"등록 닫기":"+ Rollout Batch"}
        </button>
      </header>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="rollout-kpis">
        <RolloutKpi label="Batch 학교" value={summary.schoolCount} />
        <RolloutKpi label="개통 완료" value={summary.liveSchools} tone={summary.liveSchools>0?"good":"normal"} />
        <RolloutKpi label="초대 대기" value={summary.invitationPending} tone={summary.invitationPending>0?"warn":"normal"} />
        <RolloutKpi label="Google 연결 대기" value={summary.accountPending} tone={summary.accountPending>0?"warn":"normal"} />
        <RolloutKpi label="Center 설정 대기" value={summary.centerSetupPending} tone={summary.centerSetupPending>0?"warn":"normal"} />
        <RolloutKpi label="학생·패키지 대기" value={summary.studentDataPending+summary.packagePending} tone={(summary.studentDataPending+summary.packagePending)>0?"warn":"normal"} />
      </div>

      {showCreate && (
        <section className="rollout-create-card">
          <div className="rollout-section-head">
            <div>
              <p className="eyebrow">NEW ROLLOUT BATCH</p>
              <h3>학교 일괄 개통 등록</h3>
            </div>
            <span>최대 500개교 / Batch</span>
          </div>

          <div className="rollout-create-grid">
            <label>Batch 이름
              <input value={batchName} onChange={(event)=>setBatchName(event.target.value)} placeholder="예: 전북 중학교 1차 개통" />
            </label>
            <label>예상 학년도
              <input value={yearCode} onChange={(event)=>setYearCode(event.target.value)} placeholder="2026" />
            </label>
            <label className="wide">초기 검사 패키지
              <select value={assessmentVersionId} onChange={(event)=>setAssessmentVersionId(event.target.value)}>
                <option value="">검사 자동배포 없음</option>
                {workspace.assessments.map((item)=>(
                  <option key={item.versionId} value={item.versionId}>
                    {item.instrumentName} · {item.title} · {item.versionLabel}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="rollout-badge-package">
            <div>
              <strong>Life Stamp 패키지</strong>
              <span>{selectedBadges.length}/{workspace.badges.length}종 선택</span>
            </div>
            <div>
              {workspace.badges.map((badge)=>(
                <label key={badge.badgeKey}>
                  <input type="checkbox" checked={selectedBadges.includes(badge.badgeKey)} onChange={()=>toggleBadge(badge.badgeKey)} />
                  {badge.teacherName}
                </label>
              ))}
            </div>
          </div>

          <div className="rollout-csv-card">
            <div className="rollout-csv-head">
              <div>
                <strong>학교 CSV</strong>
                <span>schoolCode, schoolName, teacherEmail 3열</span>
              </div>
              <div>
                <button className="secondary" onClick={downloadTemplate}>양식 다운로드</button>
                <button className="secondary" onClick={()=>fileRef.current?.click()}>CSV 불러오기</button>
                <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(event)=>{
                  const file=event.target.files?.[0];
                  if(file)void readFile(file);
                  event.currentTarget.value="";
                }} />
              </div>
            </div>
            <textarea
              rows={10}
              value={csvText}
              onChange={(event)=>setCsvText(event.target.value)}
              placeholder={"schoolCode,schoolName,teacherEmail\nJB-MS-001,전주예시중학교,career1@example.com\nJB-MS-002,군산예시중학교,career2@example.com"}
            />
            <div className="rollout-csv-summary">
              <span>{parsedRows.length}개 학교 인식</span>
              {csvValidation.length
                ? <strong className="attention">{csvValidation.slice(0,3).join(" · ")}</strong>
                : parsedRows.length>0
                  ? <strong className="good">형식 확인 완료</strong>
                  : <strong>CSV 대기</strong>}
            </div>
          </div>

          <div className="rollout-create-actions">
            <p>개통 담당교사에게 <b>진로교사 + 학교관리자</b> 초기 역할을 부여하는 7일 Claim 링크를 생성합니다.</p>
            <button
              className="primary"
              disabled={busy!=="" || !batchName.trim() || !parsedRows.length || csvValidation.length>0 || selectedBadges.length===0}
              onClick={createBatch}
            >
              {busy==="create"?"Batch 생성 중…":`${parsedRows.length || ""}개 학교 개통 Batch 생성`}
            </button>
          </div>
        </section>
      )}

      {Object.keys(inviteLinks).length>0 && (
        <section className="rollout-invite-export">
          <div>
            <strong>개통 담당교사 Claim 링크가 생성됐습니다.</strong>
            <p>Claim token 원문은 재조회하지 않습니다. 지금 CSV로 내려받아 전달하거나, 학교별로 다시 발급할 수 있습니다.</p>
          </div>
          <button className="primary" onClick={downloadCurrentInviteLinks}>초대링크 CSV 다운로드</button>
        </section>
      )}

      <div className="rollout-workspace">
        <aside className="rollout-batch-list">
          <div className="panel-title"><strong>Rollout Batch</strong><span>{workspace.batches.length}개</span></div>
          {workspace.batches.map((batch)=>(
            <button key={batch.batchId} className={selectedBatchId===batch.batchId?"active":""} onClick={()=>setSelectedBatchId(batch.batchId)}>
              <div><strong>{batch.batchName}</strong><span>{batch.status}</span></div>
              <p>{batch.expectedYearCode || "학년도 미지정"} · {batch.assessmentTitle || "검사 패키지 없음"}</p>
              <footer>
                <span>학교 {batch.schoolCount}</span>
                <span>Claim {batch.claimedInvites}</span>
                <span>학생준비 {batch.studentReadySchools}</span>
              </footer>
            </button>
          ))}
          {!workspace.batches.length && <p className="case-empty">아직 Rollout Batch가 없습니다.</p>}
        </aside>

        <main className="rollout-detail">
          {detail ? (
            <>
              <header className="rollout-detail-head">
                <div>
                  <p className="eyebrow">ROLLOUT PIPELINE</p>
                  <h2>{detail.batch.batchName}</h2>
                  <p>
                    {detail.batch.expectedYearCode || "학년도 미지정"} ·
                    {detail.batch.assessmentTitle?" "+detail.batch.assessmentTitle:" 검사 패키지 없음"} ·
                    Life Stamp {detail.batch.badgeKeys.length}종
                  </p>
                </div>
                {detail.batch.assessmentVersionId && (
                  <button
                    className="primary"
                    disabled={busy!=="" || distributionCandidates.length===0}
                    onClick={distributeAssessment}
                  >
                    {busy==="distribution"?"검사 배포 중…":`준비학교 검사 배포 ${distributionCandidates.length}`}
                  </button>
                )}
              </header>

              <div className="rollout-stage-strip">
                <Stage label="학교 등록" value={summary.schoolCount} />
                <Stage label="초대 Claim" value={summary.schoolCount-summary.invitationPending} />
                <Stage label="Google 연결" value={summary.schoolCount-summary.invitationPending-summary.accountPending} />
                <Stage label="Center 설정" value={summary.schoolCount-summary.invitationPending-summary.accountPending-summary.centerSetupPending} />
                <Stage label="학생 데이터" value={summary.schoolCount-summary.invitationPending-summary.accountPending-summary.centerSetupPending-summary.studentDataPending} />
                <Stage label="개통 완료" value={summary.liveSchools} emphasis />
              </div>

              <div className="rollout-toolbar">
                <input value={query} onChange={(event)=>setQuery(event.target.value)} placeholder="학교명·코드·교사 이메일 검색" />
                <select value={stateFilter} onChange={(event)=>setStateFilter(event.target.value)}>
                  <option value="ALL">전체 단계</option>
                  <option value="INVITATION_PENDING">초대 수령 대기</option>
                  <option value="ACCOUNT_LINK_PENDING">Google 연결 대기</option>
                  <option value="ACADEMIC_YEAR_PENDING">학년도 설정 대기</option>
                  <option value="CLASS_SETUP_PENDING">반 편성 대기</option>
                  <option value="STUDENT_IMPORT_PENDING">학생 등록 대기</option>
                  <option value="ASSESSMENT_PACKAGE_PENDING">검사 배포 대기</option>
                  <option value="LIVE">개통 완료</option>
                </select>
              </div>

              <div className="rollout-school-list">
                {visibleItems.map((item)=>(
                  <article key={item.itemId}>
                    <header>
                      <div>
                        <span className={"rollout-activation "+item.activationState.toLowerCase()}>{activationLabel(item.activationState)}</span>
                        {item.schoolWasExisting && <span className="rollout-existing">기존 학교</span>}
                      </div>
                      <strong>{item.schoolName}</strong>
                      <p>{item.schoolCode || "코드 없음"} · {item.teacherEmail}</p>
                    </header>

                    <div className="rollout-checklist">
                      <Check label="학교" pass={item.checklist.schoolRegistered} />
                      <Check label="초대 Claim" pass={item.checklist.invitationClaimed} />
                      <Check label="Google" pass={item.checklist.identityLinked} />
                      <Check label="학년도" pass={item.checklist.academicYearReady} />
                      <Check label="반 편성" pass={item.checklist.classesReady} />
                      <Check label="학생" pass={item.checklist.studentsReady} />
                      <Check label="검사" pass={item.checklist.assessmentReady} />
                      <Check label="Life Stamp" pass={item.checklist.badgePackageReady} />
                    </div>

                    <div className="rollout-school-metrics">
                      <span>학년도 <b>{item.yearCode || "-"}</b></span>
                      <span>반 <b>{item.classCount}</b></span>
                      <span>학생 <b>{item.studentCount}</b></span>
                      <span>검사배정 <b>{item.assessmentAssigned}</b></span>
                      <span>초대 <b>{item.invitationStatus || "-"}</b></span>
                    </div>

                    <footer>
                      <div>
                        {inviteLinks[item.schoolId] && (
                          <button className="secondary" onClick={()=>navigator.clipboard.writeText(inviteLinks[item.schoolId].claimUrl)}>새 초대링크 복사</button>
                        )}
                        {item.invitationStatus!=="CLAIMED" && (
                          <button className="secondary" disabled={busy!==""} onClick={()=>reissue(item)}>
                            {busy==="reissue:"+item.itemId?"재발급 중…":"초대 재발급"}
                          </button>
                        )}
                      </div>
                      <button className="text-button" onClick={()=>navigate("/access?organization="+encodeURIComponent(item.schoolId))}>계정·권한 →</button>
                    </footer>
                  </article>
                ))}
                {!visibleItems.length && <p className="case-empty">조건에 맞는 학교가 없습니다.</p>}
              </div>
            </>
          ):(
            <div className="rollout-empty">
              <strong>Rollout Batch를 선택하세요.</strong>
              <p>신규 Batch를 만들거나 기존 Batch를 선택하면 학교별 개통단계를 확인할 수 있습니다.</p>
            </div>
          )}
        </main>
      </div>
    </section>
  );
}

function RolloutKpi({label,value,tone="normal"}:{label:string;value:number;tone?:"normal"|"warn"|"good"}){
  return <div className={"rollout-kpi "+tone}><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong></div>;
}
function Stage({label,value,emphasis=false}:{label:string;value:number;emphasis?:boolean}){
  return <div className={emphasis?"rollout-stage emphasis":"rollout-stage"}><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong></div>;
}
function Check({label,pass}:{label:string;pass:boolean}){
  return <div className={pass?"rollout-check pass":"rollout-check"}><span>{pass?"✓":"·"}</span><b>{label}</b></div>;
}
